import * as React from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    Alert,
    Snackbar,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import PaymentsIcon from "@mui/icons-material/Payments";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LockIcon from "@mui/icons-material/Lock";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";

import {flatOuterCardSx, flatOuterCardContentSx} from "../../utils/mobileCardSx";
import {MobilePeriodBranchFilterBar} from "../Dashboard/MobilePeriodBranchFilterBar";
import PayrollWorkflowSteps from "../Payroll/PayrollWorkflowSteps";
import {useReports, formatEUR} from "../../hooks/useReports";
import {getAvatarColors, getInitials} from "../../utils/avatarUtils";
import {maskAccount} from "../../utils/maskAccount";

// ─── Managerial/HR report tiles ─────────────────────────────────────────────
// Purely presentational (icon assignment) - no backend endpoint exists yet for any of these 8
// reports (see useReports.ts's doc comment for the full list of gaps), so every tile stays
// disabled with a "not available yet" tooltip.

interface ManagerialReport {
    key: string;
    labelKey: string;
    icon: React.ReactNode;
}

const MANAGERIAL_REPORTS: ManagerialReport[] = [
    {key: "costByBranch", labelKey: "reports.managerial.items.costByBranch", icon: <BarChartIcon fontSize="small"/>},
    {key: "monthlyComparison", labelKey: "reports.managerial.items.monthlyComparison", icon: <ShowChartIcon fontSize="small"/>},
    {key: "budgetVsActual", labelKey: "reports.managerial.items.budgetVsActual", icon: <PieChartOutlineIcon fontSize="small"/>},
    {key: "bonusesDeductions", labelKey: "reports.managerial.items.bonusesDeductions", icon: <DescriptionOutlinedIcon fontSize="small"/>},
    {key: "employeesMovements", labelKey: "reports.managerial.items.employeesMovements", icon: <PeopleAltOutlinedIcon fontSize="small"/>},
    {key: "leaveAbsence", labelKey: "reports.managerial.items.leaveAbsence", icon: <CalendarMonthOutlinedIcon fontSize="small"/>},
    {key: "overtimeHours", labelKey: "reports.managerial.items.overtimeHours", icon: <AccessTimeIcon fontSize="small"/>},
    {key: "expiringContracts", labelKey: "reports.managerial.items.expiringContracts", icon: <DescriptionOutlinedIcon fontSize="small"/>},
];

/** Splits a "First Last" display name into first/last parts for getInitials/getAvatarColors. */
function splitName(name: string): { firstName: string; lastName: string } {
    const [firstName, ...rest] = name.trim().split(/\s+/).filter(Boolean);
    return {firstName: firstName ?? "", lastName: rest.join(" ")};
}

// ─── Small building blocks ──────────────────────────────────────────────────

function NotAvailableButton({
                                 label,
                                 icon,
                                 variant = "outlined",
                                 color = "inherit",
                                 fullWidth = false,
                             }: {
    label: string;
    icon: React.ReactNode;
    variant?: "outlined" | "contained";
    color?: "inherit" | "primary" | "success";
    fullWidth?: boolean;
}): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Tooltip title={t('common.notAvailableYet')}>
            <span style={{display: "block", width: fullWidth ? "100%" : "auto"}}>
                <Button variant={variant} color={color} startIcon={icon} disabled fullWidth={fullWidth}>
                    {label}
                </Button>
            </span>
        </Tooltip>
    );
}

function XlsFileBadge(): React.ReactElement {
    return (
        <Box
            sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.success.main, 0.12),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "success.dark",
            })}
        >
            <DescriptionOutlinedIcon fontSize="small"/>
            <Typography variant="caption" fontWeight={700} sx={{lineHeight: 1, mt: 0.25}}>XLS</Typography>
        </Box>
    );
}

function InlineStat({icon, value, label}: { icon: React.ReactNode; value: string; label?: string }): React.ReactElement {
    return (
        <Box
            sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
                flex: 1,
                minWidth: 0,
            })}
        >
            <Box
                sx={(theme) => ({
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: "success.dark",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                })}
            >
                {icon}
            </Box>
            <Box sx={{minWidth: 0}}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>{value}</Typography>
                {label && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{display: "block"}}>{label}</Typography>
                )}
            </Box>
        </Box>
    );
}

function ManagerialTileIconBox({children}: { children: React.ReactNode }): React.ReactElement {
    return (
        <Box
            sx={(theme) => ({
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            })}
        >
            {children}
        </Box>
    );
}

function ManagerialTile({report}: { report: ManagerialReport }): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Tooltip title={t('common.notAvailableYet')}>
            <Box
                role="button"
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    cursor: "not-allowed",
                    minWidth: 0,
                })}
            >
                <ManagerialTileIconBox>{report.icon}</ManagerialTileIconBox>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{textAlign: "left"}}>
                        {t(report.labelKey)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{textAlign: "left", display: "block"}}>
                        {t('reports.managerial.formats')}
                    </Typography>
                </Box>
                <ChevronRightIcon fontSize="small" sx={{color: "text.disabled", flexShrink: 0}}/>
            </Box>
        </Tooltip>
    );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function Reports(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const {
        isPeriodLocked,
        payrollRunLoading,
        beneficiaryCount,
        totalNetAmount,
        filteredBeneficiaryRows,
        pagedBeneficiaryRows,
        bankPage,
        bankRowsPerPage,
        handleBankPageChange,
        handleBankRowsPerPageChange,
        showAccounts,
        toggleShowAccounts,
        bankOptions,
        bankFilter,
        setBankFilter,
        atkFilename: atkFilenamePeriod,
        paySlipPeriodLabel,
        atkExpanded,
        setAtkExpanded,
        atkFieldsExpanded,
        setAtkFieldsExpanded,
        managerialExpanded,
        setManagerialExpanded,
        moreMenuAnchor,
        setMoreMenuAnchor,
        isAtkExportReady,
        atkExporting,
        atkExportError,
        exportAtkReport,
        clearAtkExportError,
        bankExportDisabledReason,
        bankExporting,
        bankExportError,
        exportBankListFile,
        clearBankExportError,
    } = useReports();

    const atkFilename = t('reports.atk.filename', {period: atkFilenamePeriod});
    const paySlipLabel = t('reports.bank.paySlipLabel', {period: paySlipPeriodLabel});
    const visibleManagerialReports = isMobile ? MANAGERIAL_REPORTS.slice(0, 2) : MANAGERIAL_REPORTS;
    const firstPreviewRow = filteredBeneficiaryRows[0];
    const firstPreviewName = firstPreviewRow ? splitName(firstPreviewRow.name) : null;
    const firstPreviewAvatarColors = firstPreviewRow ? getAvatarColors(`${firstPreviewRow.name}${firstPreviewRow.id}`) : null;

    return (
        <>
            {/* Header: title (left) + actions (right) — period/branch selection lives solely in
                the global toolbar selector (desktop) / MobilePeriodBranchFilterBar (mobile). */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    justifyContent: "space-between",
                    alignItems: {xs: "flex-start", sm: "center"},
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box sx={{textAlign: "left"}}>
                    <Typography variant="h5" sx={{fontWeight: 700, textAlign: "left"}}>
                        {t('reports.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                        {t('reports.subtitle')}
                    </Typography>
                </Box>

                {!isMobile && (
                    <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                        <NotAvailableButton label={t('reports.actions.payslips')} icon={<DescriptionOutlinedIcon/>}/>
                        <NotAvailableButton label={t('reports.actions.generateReport')} icon={<ArrowForwardIcon/>} variant="contained" color="success"/>
                    </Box>
                )}
                {isMobile && (
                    <>
                        <IconButton
                            aria-label={t('reports.actions.more')}
                            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                            sx={{border: "1px solid", borderColor: "divider"}}
                        >
                            <MoreHorizIcon/>
                        </IconButton>
                        <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
                            <Tooltip title={t('common.notAvailableYet')} placement="left">
                                <span>
                                    <MenuItem disabled>
                                        <ListItemText>{t('reports.actions.payslips')}</ListItemText>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                            <Tooltip title={t('common.notAvailableYet')} placement="left">
                                <span>
                                    <MenuItem disabled>
                                        <ListItemText>{t('reports.actions.generateReport')}</ListItemText>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        </Menu>
                    </>
                )}
            </Box>

            {isMobile && <MobilePeriodBranchFilterBar/>}

            <PayrollWorkflowSteps/>

            {isPeriodLocked && (
                <Chip
                    icon={<LockIcon/>}
                    label={t('reports.period.finalized')}
                    sx={(t2) => ({
                        backgroundColor: alpha(t2.palette.success.main, 0.12),
                        color: "success.dark",
                        fontWeight: 600,
                        mb: 3,
                        alignSelf: "flex-start",
                        "& .MuiChip-icon": {color: "success.dark"},
                    })}
                />
            )}

            {/* ATK + Bank processing */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                    gap: 3,
                }}
            >
                {/* Raporti ATK */}
                <Card sx={{backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1}}>
                    <CardContent>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
                            <Typography variant="h6" sx={{textAlign: "left"}}>{t('reports.atk.title')}</Typography>
                            {isMobile && (
                                <IconButton size="small" onClick={() => setAtkExpanded((v) => !v)}>
                                    <ExpandMoreIcon sx={{transform: atkExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s"}}/>
                                </IconButton>
                            )}
                        </Box>

                        {(!isMobile || atkExpanded) && (
                            <>
                                <Box sx={{display: "flex", flexDirection: {xs: "column", md: "row"}, gap: 2}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                        <XlsFileBadge/>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography variant="body2" fontWeight={700} sx={{textAlign: "left", wordBreak: "break-word"}}>
                                                {atkFilename}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{textAlign: "left", display: "block"}}>
                                                {t('reports.atk.subtitle')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {!isMobile && (
                                        <Box sx={{flex: 1, minWidth: 0}}>
                                            <Typography variant="body2" fontWeight={700} sx={{textAlign: "left", mb: 0.5}}>
                                                {t('reports.atk.fieldsTitle')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                                                {t('reports.atk.fieldsLine1')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                                                {t('reports.atk.fieldsLine2')}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} sx={{textAlign: "left", mb: 0.5}}>
                                                {t('reports.atk.fieldsLine3')}
                                            </Typography>
                                            <Typography variant="caption" color="text.disabled" sx={{textAlign: "left", display: "block"}}>
                                                {t('reports.atk.fieldsNote')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {isMobile && (
                                    <Box
                                        role="button"
                                        onClick={() => setAtkFieldsExpanded((v) => !v)}
                                        sx={(t2) => ({
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            border: `1px solid ${t2.palette.divider}`,
                                            borderRadius: 2,
                                            p: 1.5,
                                            mt: 2,
                                            cursor: "pointer",
                                        })}
                                    >
                                        <FormatListBulletedIcon fontSize="small" color="action"/>
                                        <Typography variant="body2" fontWeight={600} sx={{flex: 1, textAlign: "left"}}>
                                            {t('reports.atk.fieldsToggle')}
                                        </Typography>
                                        <ExpandMoreIcon fontSize="small" sx={{transform: atkFieldsExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s"}}/>
                                    </Box>
                                )}
                                {isMobile && atkFieldsExpanded && (
                                    <Box sx={{px: 1, pt: 1.5}}>
                                        <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                                            {t('reports.atk.fieldsLine1')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                                            {t('reports.atk.fieldsLine2')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{textAlign: "left", mb: 0.5}}>
                                            {t('reports.atk.fieldsLine3')}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled" sx={{textAlign: "left", display: "block"}}>
                                            {t('reports.atk.fieldsNote')}
                                        </Typography>
                                    </Box>
                                )}

                                <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} sx={{mt: 3}}>
                                    <NotAvailableButton
                                        label={isMobile ? t('reports.atk.previewMobile') : t('reports.atk.preview')}
                                        icon={<VisibilityIcon/>}
                                        fullWidth={isMobile}
                                    />
                                    <Tooltip title={isAtkExportReady ? "" : t('reports.atk.exportNotReady')}>
                                        <span style={{display: "block", width: isMobile ? "100%" : "auto"}}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                startIcon={atkExporting ? <CircularProgress size={16} color="inherit"/> : <FileDownloadIcon/>}
                                                fullWidth={isMobile}
                                                disabled={!isAtkExportReady || atkExporting}
                                                onClick={() => void exportAtkReport()}
                                            >
                                                {t('reports.atk.export')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Procesimi në bankë */}
                <Card sx={isMobile ? flatOuterCardSx(true) : {backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1}}>
                    <CardContent sx={flatOuterCardContentSx(isMobile)}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 2}}>
                            <Box
                                sx={(t2) => ({
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: alpha(t2.palette.success.main, 0.12),
                                    color: "success.dark",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                })}
                            >
                                <AccountBalanceIcon/>
                            </Box>
                            <Box sx={{minWidth: 0}}>
                                <Typography variant="h6" sx={{textAlign: "left"}}>{t('reports.bank.title')}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{textAlign: "left", display: "block"}}>
                                    {t('reports.bank.subtitle')}
                                </Typography>
                            </Box>
                        </Box>

                        {payrollRunLoading ? (
                            <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                                <CircularProgress size={24}/>
                            </Box>
                        ) : (
                            <>
                                <Stack direction="row" spacing={1.5} sx={{mb: 2}}>
                                    <InlineStat icon={<GroupsIcon fontSize="small"/>} value={String(beneficiaryCount)} label={t('reports.bank.beneficiaries')}/>
                                    <InlineStat icon={<PaymentsIcon fontSize="small"/>} value={formatEUR(totalNetAmount)}/>
                                </Stack>

                                <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} sx={{mb: 2}}>
                                    <FormControl size="small" sx={{flex: 1}}>
                                        <Select
                                            value={bankFilter}
                                            onChange={(e) => setBankFilter(e.target.value)}
                                            displayEmpty
                                            renderValue={(v) => `${t('reports.bank.bankLabel')}: ${v === 'ALL' ? t('reports.bank.allBanks') : v}`}
                                        >
                                            <MenuItem value="ALL">{t('reports.bank.allBanks')}</MenuItem>
                                            {bankOptions.map((bank) => (
                                                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title={t('common.notAvailableYet')}>
                                        <FormControl size="small" sx={{flex: 1}} disabled>
                                            <Select value="" displayEmpty renderValue={() => t('reports.bank.formatLabel')}>
                                                <MenuItem value="">{t('reports.bank.formatLabel')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Tooltip>
                                </Stack>

                                {isMobile && firstPreviewRow && firstPreviewName && firstPreviewAvatarColors && (
                                    <Tooltip title={t('common.notAvailableYet')}>
                                        <Box
                                            role="button"
                                            sx={(t2) => ({
                                                border: `1px solid ${t2.palette.divider}`,
                                                borderRadius: 2,
                                                p: 1.5,
                                                mb: 2,
                                                cursor: "not-allowed",
                                            })}
                                        >
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                <Avatar sx={{
                                                    width: 36,
                                                    height: 36,
                                                    bgcolor: firstPreviewAvatarColors.bg,
                                                    color: firstPreviewAvatarColors.color,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 600,
                                                    flexShrink: 0,
                                                }}>
                                                    {getInitials(firstPreviewName.firstName, firstPreviewName.lastName)}
                                                </Avatar>
                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                    <Box sx={{display: "flex", justifyContent: "space-between", gap: 1}}>
                                                        <Typography variant="body2" fontWeight={700} noWrap>{firstPreviewRow.name}</Typography>
                                                        <Typography variant="body2" fontWeight={700} noWrap>{formatEUR(firstPreviewRow.netAmount)}</Typography>
                                                    </Box>
                                                    <Box sx={{display: "flex", justifyContent: "space-between", gap: 1}}>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {firstPreviewRow.branch}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap>{paySlipLabel}</Typography>
                                                    </Box>
                                                    <Box sx={{mt: 0.5, textAlign: "left", overflowWrap: "anywhere"}}>
                                                        {firstPreviewRow.bankAccount ? (
                                                            <>
                                                                <Typography variant="caption" fontWeight={600} sx={{display: "block"}}>
                                                                    {firstPreviewRow.bankAccount.bankName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>
                                                                    {showAccounts ? firstPreviewRow.bankAccount.accountNumber : maskAccount(firstPreviewRow.bankAccount.accountNumber)}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">
                                                                {t('reports.bank.noAccount')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Divider sx={{my: 1}}/>
                                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('reports.bank.mobilePreviewFooter', {shown: 1, total: filteredBeneficiaryRows.length})}
                                                </Typography>
                                                <ChevronRightIcon fontSize="small" sx={{color: "text.disabled"}}/>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                )}
                                {isMobile && firstPreviewRow && (
                                    <Button
                                        variant="text"
                                        onClick={toggleShowAccounts}
                                        startIcon={showAccounts ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                        sx={{minHeight: 44, mb: 2, alignSelf: "flex-start"}}
                                    >
                                        {showAccounts ? t('reports.bank.hideAccounts') : t('reports.bank.showAccounts')}
                                    </Button>
                                )}
                                {isMobile && !payrollRunLoading && filteredBeneficiaryRows.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{textAlign: "left", mb: 2}}>
                                        {bankFilter !== 'ALL' ? t('reports.bank.emptyForBank') : t('reports.bank.empty')}
                                    </Typography>
                                )}
                            </>
                        )}

                        <Stack direction={{xs: "column", sm: "row"}} spacing={1.5}>
                            <NotAvailableButton label={t('reports.bank.checkAccounts')} icon={<SearchIcon/>} fullWidth={isMobile}/>
                            <Tooltip title={bankExportDisabledReason === "none" ? "" : t(`reports.bank.exportDisabled.${bankExportDisabledReason}`)}>
                                <span style={{display: "block", width: isMobile ? "100%" : "auto"}}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={bankExporting ? <CircularProgress size={16} color="inherit"/> : <FileDownloadIcon/>}
                                        fullWidth={isMobile}
                                        sx={isMobile ? {minHeight: 44} : undefined}
                                        disabled={bankExportDisabledReason !== "none" || bankExporting}
                                        onClick={() => void exportBankListFile()}
                                    >
                                        {t('reports.bank.exportList')}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                        {bankExportDisabledReason !== "none" && (
                            <Typography variant="caption" color="text.secondary" sx={{textAlign: "left", display: "block", mt: 1}}>
                                {t(`reports.bank.exportDisabled.${bankExportDisabledReason}`)}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* Desktop-only full bank preview table. Employee/branch/net-amount are real (from
                GET /api/payroll/run); bank/account/IBAN come from the single `bankAccount` object the
                backend resolves per employee. It is null for employees with no account yet (normal),
                and IBAN is only set for foreign accounts - both cases render a dash. */}
            {!isMobile && (
                <Card sx={{backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1, mt: 3}}>
                    <CardContent>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
                            <Typography variant="h6" sx={{textAlign: "left"}}>{t('reports.bank.previewTitle')}</Typography>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={toggleShowAccounts}
                                startIcon={showAccounts ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                            >
                                {showAccounts ? t('reports.bank.hideAccounts') : t('reports.bank.showAccounts')}
                            </Button>
                        </Box>

                        {payrollRunLoading ? (
                            <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                                <CircularProgress size={24}/>
                            </Box>
                        ) : filteredBeneficiaryRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: "left", py: 2}}>
                                {bankFilter !== 'ALL' ? t('reports.bank.emptyForBank') : t('reports.bank.empty')}
                            </Typography>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('reports.bank.table.employee')}</TableCell>
                                                <TableCell>{t('reports.bank.table.bank')}</TableCell>
                                                <TableCell>{t('reports.bank.table.iban')}</TableCell>
                                                <TableCell>{t('reports.bank.table.netAmount')}</TableCell>
                                                <TableCell>{t('reports.bank.table.reference')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pagedBeneficiaryRows.map((row) => {
                                                const rowName = splitName(row.name);
                                                const rowAvatarColors = getAvatarColors(`${row.name}${row.id}`);
                                                return (
                                                <TableRow key={row.id}>
                                                    <TableCell>
                                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                            <Avatar sx={{
                                                                width: 36,
                                                                height: 36,
                                                                bgcolor: rowAvatarColors.bg,
                                                                color: rowAvatarColors.color,
                                                                fontSize: "0.875rem",
                                                                fontWeight: 600,
                                                                flexShrink: 0,
                                                            }}>
                                                                {getInitials(rowName.firstName, rowName.lastName)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{row.branch}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.bankAccount ? (
                                                            <>
                                                                <Typography variant="body2" fontWeight={600}>{row.bankAccount.bankName}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>{showAccounts ? row.bankAccount.accountNumber : maskAccount(row.bankAccount.accountNumber)}</Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography variant="body2">—</Typography>
                                                                <Typography variant="caption" color="text.disabled" sx={{display: "block"}}>{t('reports.bank.noAccount')}</Typography>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{row.bankAccount?.iban ? (showAccounts ? row.bankAccount.iban : maskAccount(row.bankAccount.iban)) : '—'}</TableCell>
                                                    <TableCell>{formatEUR(row.netAmount)}</TableCell>
                                                    <TableCell>{paySlipLabel}</TableCell>
                                                </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    rowsPerPageOptions={[5, 10, 25]}
                                    count={filteredBeneficiaryRows.length}
                                    rowsPerPage={bankRowsPerPage}
                                    page={bankPage}
                                    onPageChange={handleBankPageChange}
                                    onRowsPerPageChange={handleBankRowsPerPageChange}
                                    labelRowsPerPage={t('reports.bank.pagination.rowsPerPage')}
                                    labelDisplayedRows={({from, to, count}) => t('reports.bank.pagination.displayedRows', {from, to, count})}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 0.5, textAlign: "left"}}>
                                    {t('reports.bank.exportNote')}
                                </Typography>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Raporte menaxheriale dhe HR */}
            <Card sx={isMobile ? {...flatOuterCardSx(true) as object, mt: 3} : {backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1, mt: 3}}>
                <CardContent sx={flatOuterCardContentSx(isMobile)}>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <Typography variant="h6" sx={{textAlign: "left"}}>{t('reports.managerial.title')}</Typography>
                            {isMobile && (
                                <Chip label={MANAGERIAL_REPORTS.length} size="small" sx={{fontWeight: 700}}/>
                            )}
                        </Box>
                        {isMobile && (
                            <IconButton size="small" onClick={() => setManagerialExpanded((v) => !v)}>
                                <ExpandMoreIcon sx={{transform: managerialExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s"}}/>
                            </IconButton>
                        )}
                    </Box>

                    {(!isMobile || managerialExpanded) && (
                        <>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", md: "repeat(4, minmax(0, 1fr))"},
                                    gap: 1.5,
                                }}
                            >
                                {visibleManagerialReports.map((report) => (
                                    <ManagerialTile key={report.key} report={report}/>
                                ))}
                            </Box>

                            {isMobile && (
                                <Tooltip title={t('common.notAvailableYet')}>
                                    <span style={{display: "block", marginTop: 16}}>
                                        <Button endIcon={<ArrowForwardIcon/>} disabled>
                                            {t('reports.managerial.viewAll', {count: MANAGERIAL_REPORTS.length})}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Snackbar
                open={Boolean(atkExportError)}
                autoHideDuration={6000}
                onClose={clearAtkExportError}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity="error" onClose={clearAtkExportError} sx={{width: "100%"}}>
                    {atkExportError}
                </Alert>
            </Snackbar>

            <Snackbar
                open={bankExportError !== null}
                autoHideDuration={6000}
                onClose={clearBankExportError}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity="error" onClose={clearBankExportError} sx={{width: "100%"}}>
                    {bankExportError || t('reports.bank.exportError')}
                </Alert>
            </Snackbar>
        </>
    );
}
